/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { LoginPage } from '@/pages/auth/LoginPage'
import { SignupPage } from '@/pages/auth/SignupPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { AcceptInvitationPage } from '@/pages/AcceptInvitationPage'
import { ProjectsPage } from '@/pages/projects/ProjectsPage'
import { ProjectDashboardPage } from '@/pages/projects/project-sections/ProjectDashboardPage'
import { ProjectClipsPage } from '@/pages/projects/project-sections/ProjectClipsPage'
import { ProjectCommitsPage } from '@/pages/projects/project-sections/ProjectCommitsPage'
import { ProjectRelationshipsPage } from '@/pages/projects/project-sections/ProjectRelationshipsPage'
import { ProjectVfxPage } from '@/pages/projects/project-sections/ProjectVfxPage'
import { ProjectTranscodesPage } from '@/pages/projects/project-sections/ProjectTranscodesPage'
import { ProjectTagsPage } from '@/pages/projects/project-sections/ProjectTagsPage'
import { ProjectTeamPage } from '@/pages/projects/project-sections/ProjectTeamPage'
import { ProjectVfxSettingsPage } from '@/pages/projects/project-sections/ProjectVfxSettingsPage'
import { AdminDashboard } from '@/pages/admin/AdminDashboard'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { useAuth } from '@/contexts/AuthContext'

// Redirect from /projects/:id to /projects/:id/dashboard
function ProjectRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/projects/${id}/dashboard`} replace />
}

function RootRedirect() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return user ? <Navigate to="/projects" replace /> : <Navigate to="/login" replace />
}

function App() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Invitation acceptance - public route that handles auth internally */}
        <Route path="/invitations/:token" element={<AcceptInvitationPage />} />

        {/* Protected routes */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />

        {/* Redirect /projects/:id to /projects/:id/dashboard */}
        <Route
          path="/projects/:id"
          element={
            <ProtectedRoute>
              <ProjectRedirect />
            </ProtectedRoute>
          }
        />

        {/* Project section routes */}
        <Route
          path="/projects/:id/dashboard"
          element={
            <ProtectedRoute>
              <ProjectDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/clips"
          element={
            <ProtectedRoute>
              <ProjectClipsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/commits"
          element={
            <ProtectedRoute>
              <ProjectCommitsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/relationships"
          element={
            <ProtectedRoute>
              <ProjectRelationshipsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/vfx"
          element={
            <ProtectedRoute>
              <ProjectVfxPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/transcodes"
          element={
            <ProtectedRoute>
              <ProjectTranscodesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/tags"
          element={
            <ProtectedRoute>
              <ProjectTagsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/team"
          element={
            <ProtectedRoute>
              <ProjectTeamPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/vfx-settings"
          element={
            <ProtectedRoute>
              <ProjectVfxSettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />

      {/* Catch all - redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
